/**
 * Product Service
 *
 * Handles all product-related API calls
 */

import api from './api';
import { extractData } from '../utils/apiHelpers';

export const productService = {
  /**
   * Get all products with filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-indexed)
   * @param {number} params.size - Items per page
   * @param {string} params.category - Category slug
   * @param {number} params.categoryId - Category ID
   * @param {boolean} params.featured - Featured products only
   * @param {string} params.tags - Filter by tags
   * @param {string} params.search - Search query
   * @returns {Promise<Object>} Products with pagination
   */
  getProducts: async (params = {}) => {
    try {
      const response = await api.get('/products', { params });
      const data = extractData(response);

      // API returns: { success, data: [...products], pagination: {...} }
      // extractData returns response.data.data (the products array)
      // pagination is a sibling of data, so access it from response.data directly
      const products = Array.isArray(data) ? data : (data?.data || data || []);
      const pagination = response.data?.pagination || {};

      return {
        success: true,
        products: Array.isArray(products) ? products : [],
        pagination
      };
    } catch (error) {
      console.error('[ProductService] Get products error:', error);
      return {
        success: false,
        products: [],
        pagination: {},
        error: error.message
      };
    }
  },

  /**
   * Get a single product by ID or slug
   * @param {number|string} idOrSlug - Product ID or slug
   * @returns {Promise<Object>} Product details
   */
  getProduct: async (idOrSlug) => {
    try {
      const response = await api.get(`/products/${idOrSlug}`);
      const data = extractData(response);

      return {
        success: true,
        product: data
      };
    } catch (error) {
      console.error('[ProductService] Get product error:', error);
      return {
        success: false,
        product: null,
        error: error.message
      };
    }
  },

  /**
   * Get all categories
   * @returns {Promise<Object>} Categories list
   */
  getCategories: async () => {
    try {
      const response = await api.get('/categories');
      const data = extractData(response);

      return {
        success: true,
        categories: data
      };
    } catch (error) {
      console.error('[ProductService] Get categories error:', error);
      throw error;
    }
  },

  /**
   * Get a single category by slug
   * @param {string} slug - Category slug
   * @returns {Promise<Object>} Category details
   */
  getCategoryBySlug: async (slug) => {
    try {
      const response = await api.get(`/categories/slug/${slug}`);
      const data = extractData(response);

      return {
        success: true,
        category: data
      };
    } catch (error) {
      console.error('[ProductService] Get category by slug error:', error);
      return {
        success: false,
        category: null,
        error: error.message
      };
    }
  },

  /**
   * Get reviews for a product
   * @param {number} productId - Product ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.sortBy - Sort option (newest, highest, lowest, helpful)
   * @returns {Promise<Object>} Reviews with pagination and summary
   */
  getProductReviews: async (productId, params = {}) => {
    try {
      const response = await api.get(`/products/${productId}/reviews`, { params });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Get reviews error:', error);
      throw error;
    }
  },

  /**
   * Search products
   * @param {string} query - Search query
   * @param {Object} params - Additional filter parameters
   * @returns {Promise<Object>} Search results
   */
  searchProducts: async (query, params = {}) => {
    try {
      const response = await api.get('/products', {
        params: { search: query, ...params }
      });
      return response.data;
    } catch (error) {
      console.error('[ProductService] Search products error:', error);
      throw error;
    }
  },

  /**
   * Get featured products
   * @param {number} limit - Number of products to fetch
   * @returns {Promise<Object>} Featured products
   */
  getFeaturedProducts: async (limit = 8) => {
    try {
      // Use query param ?featured=true as per backend guide
      const response = await api.get('/products', {
        params: { featured: true, size: limit }
      });
      const data = extractData(response);

      const products = Array.isArray(data) ? data : (data?.data || data || []);
      const pagination = response.data?.pagination || {};

      return {
        success: true,
        products: Array.isArray(products) ? products : [],
        pagination
      };
    } catch (error) {
      console.error('[ProductService] Get featured products error:', error);
      return {
        success: false,
        products: [],
        pagination: {},
        error: error.message
      };
    }
  },

  /**
   * Get products by category
   * @param {string} categorySlug - Category slug
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Products in category
   */
  getProductsByCategory: async (categorySlug, params = {}) => {
    try {
      const response = await api.get('/products', {
        params: { category: categorySlug, ...params }
      });
      const data = extractData(response);

      const products = Array.isArray(data) ? data : (data?.data || data || []);
      const pagination = response.data?.pagination || {};

      return {
        success: true,
        products: Array.isArray(products) ? products : [],
        pagination
      };
    } catch (error) {
      console.error('[ProductService] Get products by category error:', error);
      return {
        success: false,
        products: [],
        pagination: {},
        error: error.message
      };
    }
  },

  /**
   * Get products by tag
   * @param {string} tag - Tag slug to filter by
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Products with specified tag
   */
  getProductsByTag: async (tag, params = {}) => {
    try {
      const response = await api.get('/products', {
        params: { tag, ...params }
      });
      const data = extractData(response);

      const products = Array.isArray(data) ? data : (data?.data || data || []);
      const pagination = response.data?.pagination || {};

      return {
        success: true,
        products: Array.isArray(products) ? products : [],
        pagination
      };
    } catch (error) {
      console.error('[ProductService] Get products by tag error:', error);
      return {
        success: false,
        products: [],
        pagination: {},
        error: error.message
      };
    }
  },

  /**
   * Get latest arrivals (new products)
   * @param {number} limit - Number of products to fetch
   * @returns {Promise<Object>} Latest arrival products
   */
  getLatestArrivals: async (limit = 8) => {
    try {
      // Use tags=new-arrival as per backend guide
      const response = await api.get('/products', {
        params: { tags: 'new-arrival', size: limit }
      });
      const data = extractData(response);

      const products = Array.isArray(data) ? data : (data?.data || data || []);
      const pagination = response.data?.pagination || {};

      return {
        success: true,
        products: Array.isArray(products) ? products : [],
        pagination
      };
    } catch (error) {
      console.error('[ProductService] Get latest arrivals error:', error);
      return {
        success: false,
        products: [],
        pagination: {},
        error: error.message
      };
    }
  }
};

export default productService;
